import {
    Add as AddIcon,
    Delete as DeleteIcon,
    DragHandle as DragHandleIcon,
} from '@mui/icons-material';
import { Box, Button, Grid, IconButton, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ReorderListItem, SuggestionField } from 'cleveron-web-components';
import React from 'react';
import { Field, useForm } from 'react-final-form';
import { FormattedMessage } from 'react-intl';

import { notBlank } from '../../../../../../../utils/validate';

const useStyles = makeStyles(theme => ({
    deleteButton: {
        marginTop: theme.spacing(0.5),
    },
    dragButton: {
        '&:hover': {
            background: 'transparent',
        },
    },
}));

export default function InfoFormField({ disabled, fields, id, locationInfoFields }) {
    const classes = useStyles();
    const form = useForm();
    // likely stable, not subscribing to changes
    const initialLabelFields = form.getState().initialValues.labelFields;

    const moveStep = (dragIndex, hoverIndex) => {
        fields.swap(dragIndex, hoverIndex);
    };

    const deleteStep = index => {
        fields.remove(index);
    };

    const addStep = () => {
        const maxValueI = Math.max(-1, ...fields.value.map(value => value.i));
        fields.push({ i: maxValueI + 1 });
    };

    return (
        <>
            {fields.map((item, index) => {
                const stepId = `${id}Item${index}`;
                const value = fields.value[index];

                const allLabelSuggestions = new Set(locationInfoFields.map(field => field.title));
                const shouldSuggestCurrentLabel = allLabelSuggestions.has(value.title);
                // if label already used by another field, do not suggest
                fields.value.forEach(field => allLabelSuggestions.delete(field.title));
                const labelSuggestions = [...allLabelSuggestions].sort();
                if (shouldSuggestCurrentLabel) {
                    // display currently selected label first (unless it's new)
                    labelSuggestions.unshift(value.title);
                }

                let valueSuggestions = [];
                const allValueSuggestions =
                    locationInfoFields.find(field => field.title === value.title)?.values ?? [];
                const valuesMostlyUnique = allValueSuggestions.every(
                    option => option.apmIds.length < 3
                );
                const manyDifferentValues = allValueSuggestions.length > 10;
                // no suggestions for fields like MAC address
                if (!(manyDifferentValues && valuesMostlyUnique)) {
                    // to help restoring initial value for given label
                    const currentLabelInitialValue = initialLabelFields.find(
                        field => field.title === value.title
                    )?.value;
                    valueSuggestions = allValueSuggestions
                        .filter(field => field.value !== currentLabelInitialValue)
                        .map(field => field.value)
                        .sort();
                    if (currentLabelInitialValue) {
                        // offer initial value first
                        valueSuggestions.unshift(currentLabelInitialValue);
                    }
                }

                return (
                    <ReorderListItem
                        i={index}
                        id={stepId}
                        items={fields.value}
                        key={value.i}
                        moveListItem={moveStep}
                    >
                        <Grid alignItems="center" container spacing={2}>
                            <Grid item xs="auto">
                                <IconButton
                                    className={classes.dragButton}
                                    disableFocusRipple
                                    disableRipple
                                    disableTouchRipple
                                    focusRipple={false}
                                    id={`${stepId}DragButton`}
                                    size="small"
                                >
                                    <DragHandleIcon />
                                </IconButton>
                            </Grid>
                            <Grid item xs>
                                <Field
                                    TextFieldProps={{ margin: 'dense' }}
                                    component={SuggestionField}
                                    fullWidth
                                    id={`${stepId}Label`}
                                    label="Label"
                                    multiline
                                    name={`${item}.title`}
                                    options={labelSuggestions}
                                    validate={notBlank}
                                />
                            </Grid>
                            <Grid item xs>
                                <Field
                                    TextFieldProps={{ margin: 'dense' }}
                                    component={SuggestionField}
                                    fullWidth
                                    id={`${stepId}Description`}
                                    label="Description"
                                    multiline
                                    name={`${item}.value`}
                                    options={valueSuggestions}
                                    validate={notBlank}
                                />
                            </Grid>
                            {!disabled && (
                                <Grid item xs="auto">
                                    <Tooltip arrow title="Delete">
                                        <IconButton
                                            className={classes.deleteButton}
                                            id={`${stepId}DeleteButton`}
                                            onClick={() => deleteStep(index)}
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}
                        </Grid>
                    </ReorderListItem>
                );
            })}
            {!disabled && (
                <Box mt={2} xs={12}>
                    <Button
                        color="primary"
                        endIcon={<AddIcon />}
                        id={`${id}AddButton`}
                        onClick={addStep}
                        size="small"
                    >
                        <FormattedMessage
                            defaultMessage="Add label"
                            id="apm.location-info.labels.add"
                        />
                    </Button>
                </Box>
            )}
        </>
    );
}
